import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  ChevronDown,
  Check,
  Upload,
  Brain,
  Zap,
  Search,
  X,
  Wand2,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import agentService from '../../services/agentService';
import toolsService from '../../services/toolsService';
import Toast from '../Toast';
import AgentSuccessModal from '../AgentSuccessModal';
import CenterModal from '../Modal/CenterModal';
import Loaders from '../Loaders';
import { FeatureOverlay } from '../ui';
import { useSkills } from '../../hooks/useSkills';
import { formatSkillName, getSkillId, normalizeSkillsList } from '../SkillsSection/skillsUtils';

const accentColors = ['#721b1f', '#8B5CF6', '#10B981', '#F59E0B', '#EC4899', '#3B82F6', '#EF4444', '#14B8A6'];

const instructionTemplates = [
  {
    id: 'assistant',
    label: 'General Assistant',
    text: 'You are a helpful, concise assistant. Answer questions accurately and ask clarifying questions when needed.',
  },
  {
    id: 'researcher',
    label: 'Research Analyst',
    text: 'You are a rigorous research analyst. Investigate topics thoroughly, cite sources, and present structured findings with key insights and recommendations.',
  },
  {
    id: 'writer',
    label: 'Content Writer',
    text: 'You are an expert content writer. Produce clear, engaging copy tailored to the audience. Adapt tone and style on request.',
  },
  {
    id: 'coder',
    label: 'Code Engineer',
    text: 'You are a senior software engineer. Write clean, well-tested code. Explain decisions, flag edge cases, and suggest improvements.',
  },
  {
    id: 'support',
    label: 'Support Agent',
    text: 'You are a patient customer support agent. Resolve issues empathetically, escalate when appropriate, and always confirm the user\'s problem is solved.',
  },
  {
    id: 'analyst',
    label: 'Data Analyst',
    text: 'You are a data analyst. Interpret data, surface actionable insights, and present results with clear visual descriptions and caveats.',
  },
];

function parseModelProviders(modelsData) {
  const raw = modelsData?.models;
  if (!Array.isArray(raw)) return [];

  return raw.map(providerObj => {
    const providerName = Object.keys(providerObj)[0];
    const models = providerObj[providerName] || [];
    return {
      id: providerName,
      label: providerName,
      models: models.map(m => ({
        id: m.id,
        label: m.name || m.id,
      })),
    };
  });
}

function findProviderForModel(providers, modelId) {
  const provider = providers.find(p => p.models.some(m => m.id === modelId));
  return provider?.id || '';
}

function resolveIconSrc(icon) {
  if (!icon || typeof icon !== 'string') return null;
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    return icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : icon;
  }
  if (icon.startsWith('data:')) return icon;
  return `data:image/png;base64,${icon}`;
}

function normalizeIdList(value) {
  if (value == null || value === '') return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        return normalizeIdList(JSON.parse(trimmed));
      } catch {
        // fall through to comma-split
      }
    }
    return trimmed
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.flatMap(item => {
      if (item == null || item === '') return [];
      if (typeof item === 'string' || typeof item === 'number') return [String(item)];
      if (typeof item === 'object') {
        const id = item.tool_id || item.id || item.value;
        return id != null && id !== '' ? [String(id)] : [];
      }
      return [];
    });
  }
  if (typeof value === 'object') {
    const id = value.tool_id || value.id || value.value;
    return id != null && id !== '' ? [String(id)] : [];
  }
  return [];
}

function SimpleSelect({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = options.find(o => o.id === value);

  const filtered = query ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase())) : options;

  return (
    <div className="relative">
      <label className="text-sm text-[var(--text-color)] mb-1.5 block">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-white border border-shell-card-border rounded-lg px-3 py-2.5 text-sm text-[var(--text-color)] hover:border-[#d4d4d4]">
        <span className={current ? '' : 'text-muted-faint'}>
          {current ? current.label : `Select ${label.toLowerCase()}`}
        </span>
        <ChevronDown className="w-4 h-4 text-muted-dim" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-shell-card-border rounded-lg shadow-xl shadow-black/10 overflow-hidden">
            <div className="relative p-2 border-b border-shell-card-border">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-faint" />
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="w-full bg-white border border-shell-card-border rounded-md pl-8 pr-2 py-1.5 text-xs text-[var(--text-color)] placeholder:text-muted-faint outline-none focus:border-primary/40"
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 && <p className="px-3 py-2 text-xs text-muted-dim">No results found.</p>}
              {filtered.map(o => {
                const active = o.id === value;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => {
                      onChange(o.id);
                      setOpen(false);
                      setQuery('');
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-[var(--text-color)] hover:bg-black/[0.04]">
                    <span>{o.label}</span>
                    {active && <Check className="w-3.5 h-3.5 text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MultiSelect({ label, value, onToggle, options, accent }) {
  const [open, setOpen] = useState(false);
  const selected = value.length > 0;
  return (
    <div className="relative">
      <label className="text-sm text-[var(--text-color)] mb-1.5 block">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-white border border-shell-card-border rounded-lg px-3 py-2.5 text-sm text-[var(--text-color)] hover:border-[#d4d4d4]">
        <span className={selected ? '' : 'text-muted-faint'}>
          {selected ? `${value.length} selected` : 'Select to add'}
        </span>
        <ChevronDown className="w-4 h-4 text-muted-dim" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-shell-card-border rounded-lg shadow-xl shadow-black/10 py-1 max-h-48 overflow-y-auto">
            {options.length === 0 && <p className="px-3 py-2 text-xs text-muted-dim">None available</p>}
            {options.map(opt => {
              const active = value.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onToggle(opt.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-[var(--text-color)] hover:bg-black/[0.04]">
                  <span>{opt.label}</span>
                  {active && <Check className="w-3.5 h-3.5" style={{ color: accent }} />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function SettingToggle({ icon: Icon, title, enabled, onChange, enabledLabel, disabledLabel, description }) {
  return (
    <div className="bg-shell-card border border-shell-card-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-4">
        <label className="text-sm text-[var(--text-color)]">{title}</label>
        <button
          type="button"
          onClick={() => onChange(!enabled)}
          className={`relative w-10 h-6 rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-black/15'}`}>
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-4' : ''
            }`}
          />
        </button>
      </div>
      <div className="flex items-center gap-3 p-3 rounded-lg bg-[#faf9f5] border border-shell-card-border">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${enabled ? 'bg-primary/10' : 'bg-black/[0.04]'}`}>
          <Icon className={`w-4 h-4 ${enabled ? 'text-primary' : 'text-muted-dim'}`} />
        </div>
        <span className="text-sm text-[var(--text-color)]">{enabled ? enabledLabel : disabledLabel}</span>
      </div>
      <p className="text-xs text-muted mt-3 leading-relaxed">{description}</p>
    </div>
  );
}

const svgToPngFile = (svgMarkup, fileName, size = 128) =>
  new Promise((resolve, reject) => {
    const blobUrl = URL.createObjectURL(new Blob([svgMarkup], { type: 'image/svg+xml' }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      canvas.getContext('2d').drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(blobUrl);
      canvas.toBlob(blob => {
        if (blob) resolve(new File([blob], fileName, { type: 'image/png' }));
        else reject(new Error('Could not convert icon to PNG'));
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Could not load SVG icon'));
    };
    img.src = blobUrl;
  });

const createRobotIconFile = color => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
      <rect width="64" height="64" rx="14" fill="${color}" fill-opacity="0.12"/>
      <path d="M32 13v6M28 13h8" stroke="${color}" stroke-width="3" stroke-linecap="round"/>
      <rect x="16" y="19" width="32" height="27" rx="8" fill="none" stroke="${color}" stroke-width="3"/>
      <circle cx="26" cy="31" r="2.5" fill="${color}"/>
      <circle cx="38" cy="31" r="2.5" fill="${color}"/>
      <path d="M25 39h14M12 29v8M52 29v8M25 51v-5M39 51v-5" stroke="${color}" stroke-width="3" stroke-linecap="round"/>
    </svg>`;
  return svgToPngFile(svg.trim(), 'robot-icon.png');
};

export default function AgentBuilder({ agentId, isDuplicate = false }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const { user } = useContextHook(AuthContext, v => ({ user: v.user }));

  const isEdit = !!agentId && !isDuplicate;
  const [prefilled, setPrefilled] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const [name, setName] = useState('');
  const [iconColor, setIconColor] = useState(accentColors[0]);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [template, setTemplate] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [description, setDescription] = useState('');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [tools, setTools] = useState([]);
  const [skills, setSkills] = useState([]);
  const [memory, setMemory] = useState(false);
  const [triggers, setTriggers] = useState(true);

  const [promptOpen, setPromptOpen] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);

  const getAllAgentData = useQuery({
    queryKey: ['get-all-agents'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: Infinity,
  });

  const get_all_models = useQuery({
    queryKey: ['get-all-models'],
    queryFn: () => agentService.getAllModels(),
    staleTime: Infinity,
  });

  const get_single_agent = useQuery({
    queryKey: ['get-single-agent', agentId],
    queryFn: () => agentService.getAgentDetail(agentId),
    enabled: !!agentId,
  });
  const detail = get_single_agent?.data?.detail;

  const userToolsQuery = useQuery({
    queryKey: ['tools-user_created'],
    queryFn: () => toolsService.getAllTools('user_created'),
  });

  const adminToolsQuery = useQuery({
    queryKey: ['tools-admin_created'],
    queryFn: () => toolsService.getAllTools('admin_created'),
  });

  const { data: skillsData } = useSkills();

  const modelProviders = useMemo(() => parseModelProviders(get_all_models.data), [get_all_models.data]);

  const toolOptions = useMemo(() => {
    const userTools = userToolsQuery.data?.tools || [];
    const adminTools = adminToolsQuery.data?.tools || [];
    return [...userTools, ...adminTools].map(t => ({
      id: String(t.tool_id),
      label: t.tool_name,
    }));
  }, [userToolsQuery.data, adminToolsQuery.data]);

  const skillOptions = useMemo(
    () =>
      normalizeSkillsList(skillsData)
        .map(s => ({
          id: String(getSkillId(s) || ''),
          label: formatSkillName(s?.name || s?.skill_name),
        }))
        .filter(s => s.id),
    [skillsData],
  );

  useEffect(() => {
    if (!provider && modelProviders.length > 0) {
      setProvider(modelProviders[0].id);
      if (modelProviders[0].models?.[0]) {
        setModel(modelProviders[0].models[0].id);
      }
    }
  }, [modelProviders, provider]);

  useEffect(() => {
    setPrefilled(false);
  }, [agentId]);

  useEffect(() => {
    if (!agentId || prefilled || !detail || modelProviders.length === 0) return;

    setName(isDuplicate ? `${detail.agent_name || ''} Copy` : detail.agent_name || '');
    setDescription(detail.description || '');
    setInstructions(detail.instructions || '');
    setMemory(detail.enable_user_memories ?? false);
    if (detail.model_id) {
      setModel(detail.model_id);
      setProvider(findProviderForModel(modelProviders, detail.model_id));
    }
    setTools(normalizeIdList(detail.tools));
    setSkills(normalizeIdList(detail.skills));
    if (!isDuplicate) {
      const src = resolveIconSrc(detail.icon);
      if (src) setIconPreview(src);
    }
    setPrefilled(true);
  }, [agentId, isDuplicate, prefilled, detail, modelProviders]);

  const agents = getAllAgentData?.data?.agents || [];
  const atLimit = agents.length >= user?.plan?.agent_creation_limit;

  const createAgent = useMutation({
    mutationFn: payload => agentService.createAgent(payload),
    onSuccess: () => {
      setSuccessModal(true);
      queryClient.invalidateQueries({ queryKey: ['get-all-agents'] });
    },
    onError: err => {
      Toast({ type: 'error', message: err?.detail || err?.message || 'Could not create agent' });
    },
  });

  const updateAgent = useMutation({
    mutationFn: payload => agentService.EditAgent(payload),
    onSuccess: response => {
      Toast({ type: 'success', message: response?.detail || 'Agent updated' });
      queryClient.invalidateQueries({ queryKey: ['get-all-agents'] });
      navigate('/agents');
    },
    onError: err => {
      Toast({ type: 'error', message: err?.detail || err?.message || 'Could not update agent' });
    },
  });

  const selectTemplate = t => {
    setTemplate(t.id);
    setInstructions(t.text);
  };

  const toggleIn = (list, setList, val) => {
    const id = String(val);
    setList(current => (current.includes(id) ? current.filter(x => x !== id) : [...current, id]));
  };

  const handleIconUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      Toast({ type: 'error', message: 'Icon must be under 5MB' });
      return;
    }

    if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
      try {
        const svgMarkup = await file.text();
        const pngFile = await svgToPngFile(svgMarkup, file.name.replace(/\.svg$/i, '.png'));
        setIconFile(pngFile);
        setIconPreview(URL.createObjectURL(pngFile));
      } catch (error) {
        console.log(error);
        Toast({ type: 'error', message: 'Could not process SVG icon' });
      }
      return;
    }

    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const labelForId = (options, id) => options.find(o => o.id === id)?.label || id;

  const handleCreate = async () => {
    if (!name.trim() || !description.trim() || !instructions.trim() || !model) return;
    if (!isEdit && atLimit) {
      Toast({ type: 'info', message: 'Agent limit exceed' });
      return;
    }

    const payload = new FormData();
    if (isEdit) {
      payload.append('agent_id', agentId);
    }
    if (iconFile instanceof File) {
      payload.append('icon', iconFile);
    } else if (!isEdit || !iconPreview) {
      try {
        payload.append('icon', await createRobotIconFile(iconColor));
      } catch (error) {
        console.log(error);
        Toast({ type: 'error', message: 'Could not generate agent icon' });
        return;
      }
    }
    payload.append('agent_name', name.trim());
    payload.append('model_id', model);
    payload.append('domain_id', process.env.REACT_APP_DOMAIN_ID);
    payload.append('description', description.trim());
    payload.append('instructions', instructions.trim());
    payload.append('enable_user_memories', memory);
    payload.append('tools', normalizeIdList(tools).join(','));
    payload.append('skills', normalizeIdList(skills).join(','));

    if (isEdit) {
      updateAgent.mutate(payload);
    } else {
      createAgent.mutate(payload);
    }
  };

  const generatePrompt = async () => {
    if (!promptText.trim()) return;
    try {
      setPromptLoading(true);
      const resp = await agentService.generatePrompt(promptText.trim());
      if (resp.success) {
        const formatted = resp.prompt.replace(/\\n/g, '\n');
        setInstructions(formatted);
        Toast({ type: 'success', message: 'Prompt Generated Successfully' });
        setPromptOpen(false);
        setPromptText('');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPromptLoading(false);
    }
  };

  const valid = Boolean(name.trim()) && description.trim() && instructions.trim() && model;
  const activeProviderModels = modelProviders.find(p => p.id === provider)?.models || [];
  const saving = createAgent.isPending || updateAgent.isPending;

  if (agentId && get_single_agent.isFetching && !prefilled) {
    return <Loaders pageLoader />;
  }

  return (
    <>
      {successModal && <AgentSuccessModal onClose={() => setSuccessModal(false)} />}

      <CenterModal open={promptOpen} setOpen={setPromptOpen} width="600" title="Generate agent instructions">
        <div className="space-y-4">
          <textarea
            value={promptText}
            onChange={e => setPromptText(e.target.value)}
            rows={5}
            placeholder="Enter your prompt here."
            className="w-full bg-white border border-shell-card-border rounded-lg px-3 py-3 text-sm text-[var(--text-color)] placeholder:text-muted-faint outline-none focus:border-primary/40 resize-none"
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={generatePrompt}
              disabled={promptLoading || !promptText.trim()}
              className="px-5 py-2.5 rounded-lg text-sm font-medium bg-primary hover:bg-primary/90 text-white disabled:opacity-50 transition-colors">
              {promptLoading ? 'Generating...' : 'Generate prompt'}
            </button>
          </div>
        </div>
      </CenterModal>

      <div className="p-8 lg:p-10 max-w-6xl">
        <button
          type="button"
          onClick={() => navigate('/agents')}
          className="flex items-center gap-2 text-sm text-muted hover:text-[var(--text-color)] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Agents
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--text-color)] mb-2 font-serif">
            {isEdit ? 'Edit Your Agent' : 'Build Your Agent'}
          </h1>
          <p className="text-muted">
            Name your agent, define its core instructions, configure behavior, and attach tools & skills.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-5">
            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-[var(--text-color)]">Identity</h3>
              <div>
                <label className="text-sm text-[var(--text-color)] mb-1.5 block">
                  <span className="text-[#EF4444]">*</span> Agent Name
                </label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Name your agent"
                  className="w-full bg-white border border-shell-card-border rounded-lg px-3 py-2 text-sm text-[var(--text-color)] placeholder:text-muted-faint outline-none focus:border-primary/40"
                />
              </div>
              <div>
                <label className="text-sm text-[var(--text-color)] mb-1.5 block">Icon</label>
                {iconPreview ? (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={iconPreview} alt="Agent icon" className="w-full h-full object-cover" />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIconFile(null);
                        setIconPreview(null);
                      }}
                      className="flex items-center gap-1.5 text-xs text-muted hover:text-[var(--text-color)]">
                      <X className="w-3.5 h-3.5" /> Remove image
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${iconColor}1A` }}>
                      <Bot className="w-5 h-5" style={{ color: iconColor }} />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {accentColors.map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setIconColor(c)}
                          className={`w-6 h-6 rounded-full border-2 ${
                            iconColor === c ? 'border-[var(--text-color)]' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 mt-2 text-muted-dim text-xs hover:text-muted">
                  <Upload className="w-3.5 h-3.5" />
                  {iconPreview ? 'Replace image (JPG, PNG, SVG · max 5mb)' : 'Upload custom icon (JPG, PNG, SVG · max 5mb)'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.svg,image/jpeg,image/png,image/svg+xml"
                  className="hidden"
                  onChange={handleIconUpload}
                />
              </div>
            </div>

            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-[var(--text-color)]">Behavior</h3>
              <div className="grid grid-cols-2 gap-3">
                <SimpleSelect
                  label="Provider"
                  value={provider}
                  options={modelProviders.map(p => ({ id: p.id, label: p.label }))}
                  onChange={pid => {
                    setProvider(pid);
                    const p = modelProviders.find(x => x.id === pid);
                    if (p?.models?.length) setModel(p.models[0].id);
                  }}
                />
                <SimpleSelect
                  label="Model Version"
                  value={model}
                  options={activeProviderModels}
                  onChange={setModel}
                />
              </div>
            </div>

            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-[var(--text-color)]">Tools & Skills</h3>
              <MultiSelect
                label="Tools"
                value={tools}
                onToggle={v => toggleIn(tools, setTools, v)}
                options={toolOptions}
                accent="#10B981"
              />
              {tools.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tools.map(t => (
                    <span
                      key={t}
                      className="px-2.5 py-1 rounded-full text-xs bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">
                      {labelForId(toolOptions, t)}
                    </span>
                  ))}
                </div>
              )}
              <MultiSelect
                label="Skills"
                value={skills}
                onToggle={v => toggleIn(skills, setSkills, v)}
                options={skillOptions}
                accent="#F59E0B"
              />
              {skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {skills.map(s => (
                    <span
                      key={s}
                      className="px-2.5 py-1 rounded-full text-xs bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20">
                      {labelForId(skillOptions, s)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-5">
            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5">
              <FeatureOverlay
                title="Coming Soon"
                subtitle="Core Instructions is under development and will be available soon.">
                <h3 className="text-sm font-semibold text-[var(--text-color)] mb-3">Core Instructions</h3>
                <p className="text-xs text-muted mb-4">
                  Start from a template, then refine the instructions to fit your use case.
                </p>
                <div className="grid sm:grid-cols-2 gap-2 mb-4">
                  {instructionTemplates.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => selectTemplate(t)}
                      className={`text-left p-3 rounded-lg border ${
                        template === t.id ? 'border-primary bg-primary/5' : 'border-shell-card-border hover:border-[#d4d4d4]'
                      }`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-[var(--text-color)]">{t.label}</p>
                        {template === t.id && <Check className="w-3.5 h-3.5 text-primary" />}
                      </div>
                      <p className="text-xs text-muted line-clamp-2">{t.text}</p>
                    </button>
                  ))}
                </div>
              </FeatureOverlay>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm text-[var(--text-color)] block">
                  <span className="text-[#EF4444]">*</span> Instructions
                </label>
                <button
                  type="button"
                  onClick={() => setPromptOpen(true)}
                  className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80">
                  <Wand2 className="w-3.5 h-3.5" /> Generate
                </button>
              </div>
              <textarea
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                rows={10}
                placeholder="Define what your agent does, its role, and how it should behave..."
                className="w-full bg-white border border-shell-card-border rounded-lg px-3 py-3 text-sm text-[var(--text-color)] placeholder:text-muted-faint outline-none focus:border-primary/40 resize-none leading-relaxed"
              />
            </div>

            <div>
              <label className="text-sm text-[var(--text-color)] mb-1.5 block">
                <span className="text-[#EF4444]">*</span> Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Describe what this agent does"
                className="w-full bg-white border border-shell-card-border rounded-lg px-3 py-2.5 text-sm text-[var(--text-color)] placeholder:text-muted-faint outline-none focus:border-primary/40 resize-none"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <SettingToggle
                icon={Brain}
                title="Memory"
                enabled={memory}
                onChange={setMemory}
                enabledLabel="Memory Enabled"
                disabledLabel="Memory Disabled"
                description="Remember user preferences and context across conversations."
              />
              <SettingToggle
                icon={Zap}
                title="Triggers"
                enabled={triggers}
                onChange={setTriggers}
                enabledLabel="Triggers Enabled"
                disabledLabel="Triggers Disabled"
                description="Activate on events and schedules."
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => navigate('/agents')}
                className="px-5 py-2.5 text-sm text-muted hover:text-[var(--text-color)]">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={!valid || saving || (!isEdit && atLimit)}
                className="px-6 py-2.5 rounded-lg text-sm font-medium bg-primary hover:bg-primary/90 text-white disabled:opacity-50 transition-colors">
                {!isEdit && atLimit
                  ? 'Agent limit exceed'
                  : saving
                    ? isEdit
                      ? 'Updating...'
                      : 'Creating...'
                    : isEdit
                      ? 'Update Agent'
                      : 'Create Agent'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
